/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ShopperBasketsV2 } from '@/scapi';

type Basket = ShopperBasketsV2.schemas['Basket'];
type PaymentInstrument = { paymentMethodId?: string };

/**
 * Payment method ID of the $0 tender carried by zero-total orders. Shopper Orders refuses to
 * create an order without a payment instrument, so a basket fully covered by promotions carries
 * this instrument instead of a real tender. It is never authorized: order.afterPOST places the
 * order, and checkout validation rejects it on any positive total.
 */
export const NO_PAYMENT_METHOD_ID = 'NO_PAYMENT';

/** Whether an instrument is the $0 NO_PAYMENT tender. */
export function isNoPaymentInstrument(instrument: PaymentInstrument | null | undefined): boolean {
    return instrument?.paymentMethodId === NO_PAYMENT_METHOD_ID;
}

/**
 * Whether a calculated basket needs no payment: it has items and a 100%-off promotion (e.g. the
 * employee promo) has brought the order total to exactly zero. Such baskets carry only the $0
 * NO_PAYMENT instrument; order.afterPOST places the resulting order without an authorization PATCH.
 */
export function isZeroTotalBasket(basket: Basket | null | undefined): boolean {
    return Boolean(basket?.productItems?.length) && basket?.orderTotal === 0;
}

/**
 * Whether the payment step is satisfied for a zero-total basket: the NO_PAYMENT tender is the only
 * instrument and the billing address (which order validation still requires) has been submitted.
 */
export function isZeroTotalPaymentComplete(basket: Basket | null | undefined): boolean {
    const instruments = basket?.paymentInstruments ?? [];
    return (
        isZeroTotalBasket(basket) &&
        instruments.length === 1 &&
        isNoPaymentInstrument(instruments[0]) &&
        Boolean(basket?.billingAddress?.address1)
    );
}
